import { MapPin, RefreshCw, Loader2, AlertTriangle } from "lucide-react";
import { FRESH_MS } from "./useOrderLocation";

const ago = (iso) => {
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  return s < 60 ? "just now" : s < 3600 ? `${Math.round(s / 60)} min ago` : `${Math.round(s / 3600)} h ago`;
};

const settingsHint = () => (/iPhone|iPad|iPod/i.test(navigator.userAgent)
  ? "Allow it in Settings → Privacy & Security → Location Services → Safari Websites, then reload."
  : "Tap the lock/site icon in the address bar → Permissions → Location → Allow, then try again.");

// Compact order-time location status for the checkout footer. Never blocks Place Order.
export const LocationStatus = ({ geo }) => {
  const { loc, busy } = geo;
  const state = busy && loc.status !== "captured" ? "capturing" : loc.status;
  const retry = (
    <button type="button" className="os-btn text" onClick={geo.capture} disabled={busy} data-testid="checkout-location-retry">
      <RefreshCw size={12} /> {loc.status === "captured" ? "Refresh" : "Retry"}
    </button>
  );
  let body;
  if (state === "capturing") body = <><Loader2 size={14} className="animate-spin shrink-0" /><span data-testid="checkout-location-capturing">Getting your current location… You can place the order meanwhile.</span></>;
  else if (loc.status === "captured") {
    const fresh = geo.isFresh();
    body = <>
      <MapPin size={14} className="shrink-0" />
      <span data-testid="checkout-location-captured">
        {fresh ? "Current location captured" : "Location captured earlier"}{loc.accuracy_m != null ? ` · ±${Math.round(loc.accuracy_m)} m` : ""} · {ago(loc.captured_at)}
        {loc.low_accuracy && <em data-testid="checkout-location-low-accuracy"> · approximate (low GPS accuracy)</em>}
        {!fresh && <em data-testid="checkout-location-stale"> · may be outdated (older than {Math.round(FRESH_MS / 60000)} min){busy ? "" : loc.refresh_failed ? " — refresh didn’t get a new fix" : ""}</em>}
        {busy && <em data-testid="checkout-location-refreshing"> · refreshing…</em>}
      </span>
      {retry}
    </>;
  } else if (loc.status === "denied") body = <>
      <AlertTriangle size={14} className="shrink-0" />
      <span data-testid="checkout-location-denied">Location is blocked for this site. {settingsHint()} You can still place the order.</span>
      {retry}
    </>;
  else if (loc.status === "timeout") body = <><AlertTriangle size={14} className="shrink-0" /><span data-testid="checkout-location-timeout">Couldn’t get a GPS fix in time. You can retry or place the order without it.</span>{retry}</>;
  else if (loc.status === "unavailable") body = <><AlertTriangle size={14} className="shrink-0" /><span data-testid="checkout-location-unavailable">Location isn’t available on this device/browser right now. You can still place the order.</span>{retry}</>;
  else body = <><MapPin size={14} className="shrink-0" /><span data-testid="checkout-location-idle">We’ll capture your current location for this order (with your permission). You can decline and still order.</span></>;
  return <div className="text-xs text-slate-500 flex items-start gap-1.5 flex-wrap" role="status" data-testid="checkout-location-status" data-state={state} data-busy={busy ? "1" : "0"}>{body}</div>;
};
